// src/public/PublicDiscTest.js
import { useState } from 'react';
import {
    Box, Container, Paper, TextField, Button, Typography,
    CircularProgress, Alert, InputAdornment
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import { useTranslation } from 'react-i18next';
import PublicDiscTestInstructions from './PublicDiscTestInstructions';
import PublicDiscTestQuestions from './PublicDiscTestQuestions';

const TEST_TIME_LIMIT = 20; // minutos

const PublicDiscTest = () => {
    const { t } = useTranslation();
    const { testId } = useParams();
    // Email pre-llenado por query param (?email=...), usado por el portal del
    // candidato para no pedirle de nuevo un dato que ya conocemos de su sesión
    const [searchParams] = useSearchParams();

    const [currentStep, setCurrentStep] = useState('email');
    const [applicantEmail, setApplicantEmail] = useState((searchParams.get('email') || '').trim());
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [testData, setTestData] = useState(null);
    // Solo el valor con el que arranca el cronometro. El conteo vive en
    // PublicDiscTestQuestions, que es el componente que tiene las respuestas y
    // por lo tanto el unico que puede enviarlas al agotarse el tiempo.
    const [timeRemaining, setTimeRemaining] = useState(TEST_TIME_LIMIT * 60);
    const [savedResponses, setSavedResponses] = useState([]);               // ✅ se usará
    const [currentPageFromBackend, setCurrentPageFromBackend] = useState(0); // ✅ se usará

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setApplicantEmail(value);
        if (!value.trim()) return setEmailError('');
        setEmailError(isValidEmail(value) ? '' : t('disc.invalidEmail'));
    };

    const handleAccessValidation = async (e) => {
        e.preventDefault();

        if (!applicantEmail) {
            setEmailError(t('disc.pleaseEnterEmail'));
            return;
        }
        if (!isValidEmail(applicantEmail)) {
            setEmailError(t('disc.invalidEmail'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(
                `/disc-tests-public/${testId}`,
                { params: { email: applicantEmail } }
            );

            if (response.data.errorCode === 0 && response.data.response?.data) {
                const data = response.data.response.data;
                setTestData(data);

                // 'invalid' es un test que se cerró con menos de las 24 páginas.
                // Para el candidato es un test terminado igual que 'completed':
                // si no estuviera acá, la cadena de if/else no lo cubriría y al
                // reabrir el link se quedaría en una pantalla en blanco.
                if (['completed', 'invalid', 'processing', 'evaluated', 'processing_error'].includes(data.status)) {
                    setCurrentStep('completed');
                } else if (data.status === 'expired') {
                    setCurrentStep('expired');
                } else if (data.status === 'sent' || data.status === 'pending') {
                    setCurrentStep('instructions');
                } else if (data.status === 'started') {
                    // ✅ Cargar respuestas guardadas DIRECTO AL ESTADO
                    const collected = [];
                    if (data.discResults?.responses) {
                        Object.entries(data.discResults.responses).forEach(([key, value]) => {
                            const pageNum = parseInt(key.replace('page', ''), 10);
                            collected.push({
                                page: pageNum,
                                mostIndex: value.answers.mostIndex,
                                leastIndex: value.answers.leastIndex,
                            });
                        });
                    }
                    setSavedResponses(collected);

                    const startedSeconds =
                        (data.startedAt?._seconds ?? data.startedAt?.seconds ?? 0);
                    const startedTime = startedSeconds * 1000;
                    const elapsed = Math.floor((Date.now() - startedTime) / 1000);
                    const remaining = (TEST_TIME_LIMIT * 60) - elapsed;

                    // Si al retomar ya se le paso el tiempo entra con 0 y el
                    // componente de preguntas cierra el test solo, enviando lo
                    // que alcanzo a responder.
                    setTimeRemaining(remaining > 0 ? remaining : 0);

                    setCurrentPageFromBackend(Math.max(0, (data.discResults?.currentPage ?? 1) - 1));
                    
                    setCurrentStep('test');
                }
            } else {
                setError(response.data.errorMessage || t('disc.loadError'));
                setCurrentStep('error');
            }
        } catch (err) {
            console.error('Error validating access:', err);
            if (err.response?.status === 403) {
                setError(t('disc.accessDenied'));
            } else if (err.response?.status === 404) {
                setError(t('disc.testNotFound'));
            } else if (err.response?.status === 410) {
                setCurrentStep('expired');
            } else {
                setError(err.response?.data?.errorMessage || t('disc.loadError'));
            }
            setCurrentStep('error');
        } finally {
            setLoading(false);
        }
        // ❌ No retornes nada; el flujo usa estado.
    };

    const handleStartTest = async () => {
        setLoading(true);
        try {
            const response = await api.put(
                `/disc-tests-public/${testId}/start`,
                { applicantEmail }
            );

            if (response.data.errorCode === 0) {
                setTimeRemaining(TEST_TIME_LIMIT * 60); // ✅ inicia contador limpio
                setCurrentStep('test');
            } else {
                setError(response.data.errorMessage || t('disc.errorStartingTest'));
                setCurrentStep('error');
            }
        } catch (err) {
            console.error('Error starting test:', err);
            setError(err.response?.data?.errorMessage || t('disc.errorStartingTest'));
            setCurrentStep('error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Cierre del test. `expiredByTime` lo manda PublicDiscTestQuestions cuando
     * se agoto el cronometro: en ese caso las respuestas YA se enviaron al
     * backend, aca solo cambia el mensaje que ve el candidato.
     */
    const handleCompleteTest = (testResults) => {
        setTestData((prev) => ({ ...prev, ...testResults }));
        setCurrentStep('completed');
    };

    const renderEmailStep = () => (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        {t('disc.emailVerification')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('disc.enterEmailToAccess')}
                    </Typography>
                </Box>

                <form onSubmit={handleAccessValidation}>
                    <TextField
                        fullWidth
                        type="email"
                        label={t('disc.enterEmail')}
                        value={applicantEmail}
                        onChange={handleEmailChange}
                        error={!!emailError}
                        helperText={emailError}
                        disabled={loading}
                        autoFocus
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon sx={{ color: 'action.active' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading || !applicantEmail || !!emailError}
                        size="large"
                    >
                        {loading ? <CircularProgress size={24} /> : t('disc.accessTest')}
                    </Button>
                </form>
            </Paper>
        </Container>
    );

    const renderError = () => (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('disc.errorTitle')}
                </Typography>
                <Typography variant="body2">
                    {error || t('disc.genericError')}
                </Typography>
            </Alert>
        </Container>
    );

    const renderExpired = () => (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Alert severity="warning">
                <Typography variant="h6" gutterBottom>
                    {t('disc.testExpired')}
                </Typography>
                <Typography variant="body2">
                    {t('disc.testExpiredMessage')}
                </Typography>
            </Alert>
        </Container>
    );

    const renderCompleted = () => (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            {testData?.expiredByTime ? (
                <Alert severity="warning">
                    <Typography variant="h6" gutterBottom>
                        {t('disc.timeUpTitle')}
                    </Typography>
                    <Typography variant="body2">
                        {testData?.submitFailed
                            ? t('disc.timeUpSubmitFailedMessage')
                            : t('disc.timeUpMessage')}
                    </Typography>
                </Alert>
            ) : (
                <Alert severity="success">
                    <Typography variant="h6" gutterBottom>
                        {t('disc.testCompletedTitle')}
                    </Typography>
                    <Typography variant="body2">
                        {t('disc.testCompletedMessage')}
                    </Typography>
                </Alert>
            )}
        </Container>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
            {currentStep === 'email' && renderEmailStep()}
            {currentStep === 'error' && renderError()}
            {currentStep === 'expired' && renderExpired()}
            {currentStep === 'instructions' && (
                <PublicDiscTestInstructions
                    testData={testData}
                    onStart={handleStartTest}
                    loading={loading}
                />
            )}
            {currentStep === 'test' && (
                <PublicDiscTestQuestions
                    testId={testId}
                    language={testData.language}
                    initialTime={timeRemaining}
                    onComplete={handleCompleteTest}
                    initialResponses={savedResponses}
                    initialPage={currentPageFromBackend}
                />
            )}
            {currentStep === 'completed' && renderCompleted()}
        </Box>
    );
};

export default PublicDiscTest;
